import { useCallback } from 'react';
import { Inbox } from 'lucide-react';
import styles from './EntityList.module.css';

function SkeletonRow({ colCount }) {
  return (
    <tr className={styles.skeletonRow}>
      {Array.from({ length: colCount }).map((_, i) => (
        <td key={i} className={styles.skeletonCell}>
          <span className={styles.shimmer} />
        </td>
      ))}
    </tr>
  );
}

export default function EntityList({
  title,
  columns,
  items,
  total,
  loading,
  errorMessage,
  onRowClick,
  isAdmin,
  selectedIds,
  onSelectionChange,
  onArchive,
  filterPanel,
  page,
  totalPages,
  onPrev,
  onNext,
  addButton,
  showArchived,
  onUnarchive,
}) {
  const allSelected =
    items.length > 0 && selectedIds && items.every((item) => selectedIds.includes(item.id));

  const handleSelectAll = useCallback(
    (e) => {
      if (!onSelectionChange) return;
      if (e.target.checked) {
        onSelectionChange(items.map((item) => item.id));
      } else {
        onSelectionChange([]);
      }
    },
    [items, onSelectionChange]
  );

  const handleSelectRow = useCallback(
    (id, checked) => {
      if (!onSelectionChange || !selectedIds) return;
      if (checked) {
        onSelectionChange([...selectedIds, id]);
      } else {
        onSelectionChange(selectedIds.filter((sid) => sid !== id));
      }
    },
    [selectedIds, onSelectionChange]
  );

  const handleArchive = useCallback(() => {
    if (onArchive && selectedIds && selectedIds.length > 0) {
      onArchive(selectedIds);
    }
  }, [onArchive, selectedIds]);

  const selectedCount = selectedIds ? selectedIds.length : 0;
  const showArchive = isAdmin && selectedCount > 0;

  const colCount = columns.length;

  return (
    <div className={styles.page}>
      {errorMessage && (
        <p className={styles.fetchError} role="alert">{errorMessage}</p>
      )}

      {/* Page header */}
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>{title}</h1>
        {addButton && <div className={styles.addSlot}>{addButton}</div>}
      </div>

      {/* Card */}
      <div className={styles.card}>
        {/* Filter panel (optional — renders above table) */}
        {filterPanel && (
          <div className={styles.filterPanel}>{filterPanel}</div>
        )}

        {/* Table */}
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key} className={styles.th}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0
                ? Array.from({ length: 5 }).map((_, i) => (
                    <SkeletonRow key={i} colCount={colCount} />
                  ))
                : items.map((item, rowIndex) => {
                    const isSelected = selectedIds && selectedIds.includes(item.id);
                    const isLast = rowIndex === items.length - 1;
                    return (
                      <tr
                        key={item.id}
                        className={`${styles.row} ${isSelected ? styles.rowSelected : ''} ${isLast ? styles.rowLast : ''}`}
                        onClick={() => onRowClick && onRowClick(item)}
                        style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                      >
                        {columns.map((col) => (
                          <td key={col.key} className={styles.td}>
                            {col.render ? col.render(item[col.key], item) : item[col.key]}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
            </tbody>
          </table>

          {/* Empty state */}
          {!loading && items.length === 0 && (
            <div className={styles.empty}>
              <Inbox size={32} strokeWidth={1.5} className={styles.emptyIcon} />
              <p className={styles.emptyText}>No records found</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <span className={styles.footerCount}>
            {total ?? 0} total
          </span>
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.pageBtn}
                onClick={onPrev}
                disabled={page <= 1 || loading}
                aria-label="Previous page"
              >
                ‹ Prev
              </button>
              <span className={styles.pageLabel}>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className={styles.pageBtn}
                onClick={onNext}
                disabled={page >= totalPages || loading}
                aria-label="Next page"
              >
                Next ›
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
